import Breadcrumb from "../../core/common/Breadcrumb/breadcrumb";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import { courses } from "./types/courses";
import { useTranslation } from "react-i18next";

const truncate = (text: string | undefined, max = 120) =>
  (text ?? "").length > max ? (text ?? "").slice(0, max) + "..." : (text ?? "");

const Courses = () => {
  const { t } = useTranslation();
  return (
    <>
      <Breadcrumb title={t("courseGrid.breadcrumb")} />
      <section className="bg-gray-50 from-[#e0f7ff] via-[#f0fbff] to-[#f9fdff] py-14 font-sans">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-[#009fe3] mb-4 tracking-tight">
              {t("courses.heading")}
            </h2>
            <div className="mx-auto w-16 h-1 bg-[#009fe3] rounded-full mb-5"></div>
            <p className="text-gray-700 text-base leading-relaxed">
              {t("courseGrid.subheading")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-10">
            {courses.map((course) => (
              <div
                key={course.slug}
                className="bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col"
              >
                <Link to={`/course-details/${course.slug}`}>
                  <div
                    className="w-full bg-gray-100 flex items-center justify-center"
                    style={{ minHeight: 300, maxHeight: 380 }}
                  >
                    <ImageWithBasePath
                      src={course.image}
                      alt={course.title}
                      className="max-w-full max-h-[300px] object-contain transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                </Link>

                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-semibold text-center text-[#009fe3] mt-2 hover:text-[#007cb5] transition-colors duration-200">
                    <Link to={`/course-details/${course.slug}`}>
                      {course.i18nKey ? t(`courses.${course.i18nKey}.title`) : course.title}
                    </Link>
                  </h3>
                  {course.discontinued && (
                    <p className="text-center mt-1">
                      <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full border border-amber-300">
                        Not currently offered
                      </span>
                    </p>
                  )}
                  <div className="w-10 h-1 bg-[#009fe3] rounded-full mx-auto my-2"></div>
                  <p className="text-gray-600 text-base mb-4 flex-1">
                    {course.i18nKey ? t(`courses.${course.i18nKey}.description`) : truncate(course.description)}
                  </p>

                  <div className="flex items-end justify-between mt-4">
                    <div>
                      {/* Single price courses */}
                      {course.price && (
                        <div className="flex flex-col gap-0.5">
                          {/* Old price — show only if discounted */}
                          {course.discountedPrice && (
                            <span className="text-gray-400 line-through text-sm">
                              {course.price.toLocaleString()} BDT
                            </span>
                          )}
                          {/* New price + badge */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[#009fe3] font-bold text-lg">
                              {(
                                course.discountedPrice ?? course.price
                              ).toLocaleString()}{" "}
                              BDT
                            </span>
                            {course.discountedPrice && (
                              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                30% OFF
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* German Language multi-level breakdown */}
                      {(course.priceBreakdown ||
                        course.discountedPriceBreakdown) && (
                        <div className="flex flex-col gap-1">
                          {/* Badge on top */}
                          {course.discountedPriceBreakdown && (
                            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full w-fit">
                              30% OFF
                            </span>
                          )}
                          {/* Level rows: old + new side by side */}
                          {course.discountedPriceBreakdown
                            ? course.discountedPriceBreakdown.map(
                                (newPrice, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center gap-2 text-sm"
                                  >
                                    <span className="text-gray-400 line-through">
                                      {course.priceBreakdown?.[index]}
                                    </span>
                                    <span className="text-[#009fe3] font-bold">
                                      → {newPrice}
                                    </span>
                                  </div>
                                ),
                              )
                            : course.priceBreakdown?.map((price, index) => (
                                <div
                                  key={index}
                                  className="text-[#009fe3] font-semibold text-sm"
                                >
                                  {price}
                                </div>
                              ))}
                        </div>
                      )}
                    </div>

                    <Link
                      to={`/course-details/${course.slug}`}
                      className="bg-[#009fe3] hover:bg-[#007cb5] text-white font-semibold py-2 px-5 rounded-lg shadow transition"
                    >
                      {t("courseDetail.learnMore")}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Courses;
